import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Paper,
  IconButton,
  Card,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Fade,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Upload as UploadIcon, Delete as DeleteIcon, CheckCircle, Restaurant, Info, Settings, LocationOn, Business, Key, Search as SearchIcon } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { useDropzone } from 'react-dropzone';
import { styled, keyframes } from '@mui/material/styles';

// Keyframe for fade-in animation
const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

// Keyframe for pulse animation on active step
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(33, 204, 187, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(33, 204, 187, 0); }
  100% { box-shadow: 0 0 0 0 rgba(33, 204, 187, 0); }
`;

// Styled Paper with Gradient Border
const StyledPaper = styled(Paper)(({ theme }) => ({
  maxWidth: 1000,
  margin: '0 auto',
  padding: theme.spacing(4),
  borderRadius: '24px',
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.08)',
  background: '#ffffff',
  border: '2px solid transparent',
  backgroundClip: 'padding-box',
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: -2,
    left: -2,
    right: -2,
    bottom: -2,
    zIndex: -1,
    borderRadius: '24px',
    background: 'linear-gradient(45deg, #21ccbb, #26e0cc)',
  },
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 15px 50px rgba(33, 204, 187, 0.15)',
  },
  animation: `${fadeIn} 0.6s ease-out`,
}));

// Styled Button with Gradient and Glow
const StyledButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #21ccbb 30%, #26e0cc 90%)',
  color: '#fff',
  borderRadius: '30px',
  padding: theme.spacing(1.5, 6),
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  boxShadow: '0 5px 15px rgba(33, 204, 187, 0.4)',
  transition: 'all 0.4s ease',
  '&:hover': {
    background: 'linear-gradient(45deg, #1ab7a8 30%, #21ccbb 90%)',
    boxShadow: '0 8px 25px rgba(33, 204, 187, 0.6)',
    transform: 'translateY(-3px) scale(1.02)',
  },
  '&:disabled': {
    background: '#E0E0E0',
    color: '#9E9E9E',
    boxShadow: 'none',
  },
  animation: `${fadeIn} 0.8s ease-out`,
}));

// Styled Dialog for Popup
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    background: 'linear-gradient(45deg, #ffffff, #f9fafb)',
    boxShadow: '0 10px 30px rgba(33, 204, 187, 0.2)',
    padding: theme.spacing(2),
    minWidth: '300px',
    maxWidth: '500px',
    textAlign: 'center',
    animation: `${fadeIn} 0.4s ease-out`,
  },
}));

const villes = [
  { id: 1, name: 'CASABLANCA' },
  { id: 3, name: 'RABAT' },
  { id: 5, name: 'MARRAKECH' },
  { id: 6, name: 'FES' },
  { id: 7, name: 'TANGER' },
  { id: 8, name: 'MOHAMMEDIA' },
  { id: 11, name: "M'diq" },
  { id: 13, name: 'Maroc' },
  { id: 14, name: 'AGADIR' },
  { id: 15, name: 'DAR BOUAZZA' },
  { id: 16, name: 'BOUSKOURA' },
  { id: 17, name: 'OUJDA' },
  { id: 18, name: 'MEKNES' },
  { id: 19, name: 'TEMARA' },
];

// File Upload Component
const FileUpload = ({ name, label, onFileChange, file, error }) => {
  const [isLoading, setIsLoading] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      setIsLoading(true);
      setTimeout(() => {
        if (acceptedFiles.length > 0) {
          const selectedFile = acceptedFiles[0];
          if (selectedFile.size > 5 * 1024 * 1024) {
            alert('La taille du fichier dépasse la limite de 5 Mo');
          } else {
            onFileChange({ target: { name, files: [selectedFile], type: 'file' } });
          }
        } else if (rejectedFiles.length > 0) {
          alert('Type de fichier non valide. Veuillez télécharger une image JPEG ou PNG.');
        }
        setIsLoading(false);
      }, 500);
    },
    [name, onFileChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png'] },
    multiple: false,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', animation: `${fadeIn} 0.6s ease-out` }}>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1.5,
          color: '#2D2D2D',
          fontWeight: 600,
          fontSize: '0.95rem',
          letterSpacing: '0.2px',
        }}
      >
        {label}
      </Typography>
      <Card
        {...getRootProps()}
        sx={{
          border: `2px dashed ${error ? '#FF6F61' : '#E0E0E0'}`,
          borderRadius: '12px',
          bgcolor: isDragActive ? '#E6F7F5' : '#F9FAFB',
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: '#21ccbb',
            bgcolor: '#E6F7F5',
            transform: 'scale(1.01)',
          },
          minHeight: '170px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
          position: 'relative',
        }}
      >
        <input {...getInputProps()} />
        {isLoading ? (
          <CircularProgress size={40} sx={{ color: '#21ccbb' }} />
        ) : file ? (
          <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img
              src={file ? URL.createObjectURL(file) : 'https://via.placeholder.com/100?text=Échec+Image'}
              alt="Aperçu"
              style={{
                maxWidth: '100%',
                maxHeight: '180px',
                objectFit: 'contain',
                borderRadius: '8px',
                border: '2px solid #21ccbb',
              }}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/100?text=Échec+Image';
              }}
            />
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onFileChange({ target: { name, files: [], type: 'file' } });
              }}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: '#FF6F61',
                bgcolor: '#fff',
                '&:hover': { color: '#E63946', bgcolor: '#fff' },
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              <DeleteIcon />
            </IconButton>
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 8,
                color: '#2D2D2D',
                bgcolor: 'rgba(255,255,255,0.8)',
                px: 1,
                borderRadius: '4px',
              }}
            >
              {file.name} ({(file.size / 1024).toFixed(2)} Ko)
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <UploadIcon sx={{ fontSize: 40, color: '#21ccbb', mb: 1, transition: 'color 0.3s ease' }} />
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.9rem', fontWeight: 500 }}>
              {isDragActive ? 'Déposez l\'image ici' : 'Glissez-déposez une image ou cliquez pour sélectionner'}
            </Typography>
          </Box>
        )}
      </Card>
      {error && (
        <Typography color="error" variant="caption" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

const CreateRestaurant = () => {
  const [formData, setFormData] = useState({
    restaurant_id: '',
    designation: '',
    image: null,
    background: null,
    description: '',
    url_unique: '',
    type_boutique: '',
    pre_commande: false,
    frais_service: 0,
    fidelite_service: false,
    prc_fidelite: 0,
    max_jour_fidelite: 0,
    date_fidelite: '',
    type_abonnement: 'Avec Livreur',
    comission_cb: 0,
    comission_cash: 0,
    adresse: '',
    type: 'Restaurant',
    lat: '0',
    lng: '0',
    ville: 'CASABLANCA',
    ville_id: 1,
    nom_societe: '',
    rib: '',
    email: '',
    ice: '',
    store_id: uuidv4(),
    nom_boutique: '',
    lien_google_maps: '',
    livraison: true,
    emporter: false,
    num_telephone: '',
    moyen_de_paiement: 'Espèce & Carte bancaire',
    lien_facebook: '',
    lien_instagram: '',
    quiSommesNous: '',
  });

  const [storeIdTouched, setStoreIdTouched] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialog, setDialog] = useState({ open: false, message: '', severity: 'success' });
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [emailSearch, setEmailSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  useEffect(() => {
    if (!storeIdTouched) {
      setFormData((prev) => ({ ...prev, store_id: uuidv4() }));
    }
  }, [storeIdTouched]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (files && files.length !== undefined) {
      const newFile = files.length > 0 ? files[0] : null;
      setFormData((prev) => ({ ...prev, [name]: newFile }));
      setErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      setErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    if (name === 'ville') {
      const selectedVille = villes.find((v) => v.name === value);
      setFormData((prev) => ({
        ...prev,
        ville: value,
        ville_id: selectedVille ? selectedVille.id : prev.ville_id,
      }));
      setErrors((prev) => ({ ...prev, ville: '', ville_id: '' }));
      return;
    }

    if (name === 'restaurant_id') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
      setErrors((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleStoreIdClick = () => {
    setStoreIdTouched(true);
  };

  const handleSearchModalOpen = () => {
    setSearchModalOpen(true);
    setEmailSearch('');
    setSearchResults([]);
    setSearchError('');
  };

  const handleSearchModalClose = () => {
    setSearchModalOpen(false);
    setEmailSearch('');
    setSearchResults([]);
    setSearchError('');
  };

  const handleEmailSearch = async () => {
    if (!emailSearch.trim()) {
      setSearchError('Veuillez entrer un email.');
      return;
    }

    setSearchLoading(true);
    setSearchError('');

    try {
      const response = await fetch('https://apibackoffice.lalivraison.ma/api/restaurants/search-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ email: emailSearch.trim() }),
      });

      const data = await response.json();
      if (response.ok) {
        setSearchResults(data.accounts || []);
        if (data.accounts.length === 0) {
          setSearchError('Aucun compte trouvé pour cet email.');
        }
      } else {
        setSearchError(data.message || 'Erreur lors de la recherche.');
      }
    } catch (error) {
      setSearchError('Erreur lors de la recherche. Veuillez réessayer.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectAccount = (account) => {
    setFormData((prev) => ({
      ...prev,
      restaurant_id: account.id_caisse,
      email: account.email,
    }));
    setErrors((prev) => ({ ...prev, restaurant_id: '', email: '' }));
    handleSearchModalClose();
  };

  const validateStep = (step) => {
    const newErrors = {};
    const requiredFields = {
      0: ['restaurant_id'],
      1: ['image', 'background'],
      2: ['designation', 'url_unique', 'type_abonnement'],
      3: ['comission_cb', 'comission_cash'],
      4: ['type', 'ville', 'adresse', 'livraison'],
      5: [],
      6: ['num_telephone'],
    };

    requiredFields[step]?.forEach((field) => {
      const value = formData[field];
      if (!value || (['image', 'background'].includes(field) && !(value instanceof File))) {
        newErrors[field] = 'Ce champ est requis';
      }
    });

    if (step === 0 && formData.restaurant_id && !/^\d+$/.test(formData.restaurant_id)) {
      newErrors.restaurant_id = "L'ID Restaurant doit contenir uniquement des chiffres";
    }

    if (step === 4 && formData.type_abonnement === 'Avec Livreur') {
      if (!formData.lat || formData.lat === '0') {
        newErrors.lat = 'La latitude est requise pour Avec Livreur';
      }
      if (!formData.lng || formData.lng === '0') {
        newErrors.lng = 'La longitude est requise pour Avec Livreur';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleStep = (step) => () => {
    if (validateStep(activeStep)) {
      setActiveStep(step);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(activeStep)) return;

    setIsSubmitting(true);
    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === 'image' || key === 'background') {
        if (formData[key]) formDataToSend.append(key, formData[key]);
      } else {
        formDataToSend.append(key, formData[key] === null || formData[key] === undefined ? '' : formData[key]);
      }
    });

    try {
      const response = await fetch('https://apibackoffice.lalivraison.ma/api/restaurants', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formDataToSend,
      });
      if (response.ok) {
        setDialog({ open: true, message: 'Restaurant créé avec succès !', severity: 'success' });
        setFormData({
          restaurant_id: '',
          designation: '',
          image: null,
          background: null,
          description: '',
          url_unique: '',
          type_boutique: '',
          pre_commande: false,
          frais_service: 0,
          fidelite_service: false,
          prc_fidelite: 0,
          max_jour_fidelite: 0,
          date_fidelite: '',
          type_abonnement: 'Avec Livreur',
          comission_cb: 0,
          comission_cash: 0,
          adresse: '',
          type: 'Restaurant',
          lat: '0',
          lng: '0',
          ville: 'CASABLANCA',
          ville_id: 1,
          nom_societe: '',
          rib: '',
          email: '',
          ice: '',
          store_id: uuidv4(),
          nom_boutique: '',
          lien_google_maps: '',
          livraison: true,
          emporter: false,
          num_telephone: '',
          moyen_de_paiement: 'Espèce & Carte bancaire',
          lien_facebook: '',
          lien_instagram: '',
          quiSommesNous: '',
        });
        setActiveStep(0);
      } else {
        const errorData = await response.json();
        setDialog({ open: true, message: `Erreur : ${errorData.message}`, severity: 'error' });
      }
    } catch (error) {
      console.error('Erreur:', error);
      setDialog({ open: true, message: 'Erreur lors de la création du restaurant. Veuillez réessayer.', severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, open: false });
  };

  // Unified Input Styling
  const inputStyles = {
    bgcolor: '#F9FAFB',
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: '#E5E7EB', borderWidth: '1px' },
      '&:hover fieldset': { borderColor: '#26e0cc' },
      '&.Mui-focused fieldset': {
        borderColor: '#21ccbb',
        boxShadow: '0 0 12px rgba(33, 204, 187, 0.15)',
      },
      '& input, & textarea': {
        padding: '14px 16px',
        fontSize: '0.95rem',
        color: '#2D2D2D',
        fontWeight: 500,
        lineHeight: '1.5',
      },
      height: '56px',
      '&.MuiInputBase-multiline': {
        height: 'auto',
        padding: 0,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#6B7280',
      fontWeight: 500,
      fontSize: '0.95rem',
      letterSpacing: '0.2px',
      transform: 'translate(14px, 16px) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#21ccbb',
    },
    '& .MuiInputBase-root.Mui-error': {
      '& fieldset': { borderColor: '#FF6F61' },
    },
    '&:hover': {
      transform: 'translateY(-1px)',
    },
    animation: `${fadeIn} 0.5s ease-out`,
  };

  const selectStyles = {
    ...inputStyles,
    '& .MuiSelect-select': {
      padding: '14px 16px',
      fontSize: '0.95rem',
      color: '#2D2D2D',
      fontWeight: 500,
      lineHeight: '1.5',
      height: '24px',
    },
  };

  // Custom Step Icon
  const CustomStepIcon = (props) => {
    const { active, completed, icon } = props;
    const icons = {
      1: <Key />,
      2: <UploadIcon />,
      3: <Info />,
      4: <Settings />,
      5: <LocationOn />,
      6: <Business />,
      7: <Restaurant />,
    };

    return (
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: active ? '#21ccbb' : completed ? '#4CAF50' : '#E0E0E0',
          color: '#fff',
          fontSize: '1.2rem',
          fontWeight: 600,
          transition: 'all 0.3s ease',
          animation: active ? `${pulse} 2s infinite` : 'none',
          '&:hover': {
            transform: 'scale(1.1)',
            boxShadow: '0 0 15px rgba(33, 204, 187, 0.5)',
          },
        }}
      >
        {completed ? <CheckCircle /> : icons[icon]}
      </Box>
    );
  };

  const steps = [
    {
      label: 'ID Restaurant',
      content: (
        <Grid container spacing={3} sx={{ minHeight: { xs: '400px', md: '500px' }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <Grid item xs={12} sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: '20px' }}>
              <Key sx={{ fontSize: 60, color: '#21ccbb', animation: `${fadeIn} 0.6s ease-out` }} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                color: '#2D2D2D',
                fontWeight: 600,
                mb: 2,
                animation: `${fadeIn} 0.6s ease-out`,
              }}
            >
              Entrez votre ID Restaurant
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#6B7280',
                mb: 3,
                maxWidth: 600,
                mx: 'auto',
                animation: `${fadeIn} 0.6s ease-out`,
              }}
            >
              Fournissez l'identifiant unique du restaurant (ID Caisse) pour commencer la configuration de votre restaurant. Cet identifiant servira à identifier votre restaurant dans le système.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, maxWidth: 500, mx: 'auto' }}>
              <TextField
                label="ID Restaurant (ID Caisse)"
                name="restaurant_id"
                value={formData.restaurant_id}
                onChange={handleChange}
                fullWidth
                required
                variant="outlined"
                type="text"
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                sx={{
                  ...inputStyles,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#E5E7EB', borderWidth: '2px' },
                    '&:hover fieldset': { borderColor: '#21ccbb' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#21ccbb',
                      boxShadow: '0 0 12px rgba(33, 204, 187, 0.3)',
                    },
                  },
                }}
                error={!!errors.restaurant_id}
                helperText={errors.restaurant_id}
              />
              <StyledButton
                onClick={handleSearchModalOpen}
                startIcon={<SearchIcon />}
                sx={{ padding: '14px 20px' }}
              >
                Rechercher
              </StyledButton>
            </Box>
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Images du Restaurant',
      content: (
        <Grid container spacing={3} sx={{ minHeight: { xs: '400px', md: '500px' }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Grid item xs={12} sm={6}>
            <FileUpload
              name="image"
              label="LOGO"
              onFileChange={handleChange}
              file={formData.image}
              error={errors.image}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FileUpload
              name="background"
              label="ARRIÈRE-PLAN"
              onFileChange={handleChange}
              file={formData.background}
              error={errors.background}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Informations de Base',
      content: (
        <Grid container spacing={3} sx={{ minHeight: { xs: '400px', md: '500px' }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Désignation"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={inputStyles}
              error={!!errors.designation}
              helperText={errors.designation}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Type de Boutique"
              name="type_boutique"
              value={formData.type_boutique}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={inputStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="URL Unique"
              name="url_unique"
              value={formData.url_unique}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (e.key === ' ') e.preventDefault();
              }}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\s/g, '');
              }}
              fullWidth
              required
              variant="outlined"
              sx={inputStyles}
              error={!!errors.url_unique}
              helperText={errors.url_unique}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Type d'Abonnement</InputLabel>
              <Select
                label="Type d'Abonnement"
                name="type_abonnement"
                value={formData.type_abonnement}
                onChange={handleChange}
                sx={selectStyles}
                error={!!errors.type_abonnement}
              >
                <MenuItem value="Avec Livreur">Avec Livreur</MenuItem>
                <MenuItem value="Sans Livreur">Sans Livreur</MenuItem>
              </Select>
              {errors.type_abonnement && (
                <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                  {errors.type_abonnement}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              sx={inputStyles}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Configuration des Services',
      content: (
        <Grid container spacing={3} sx={{ minHeight: { xs: '400px', md: '500px' }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Frais de Service"
              name="frais_service"
              type="number"
              value={formData.frais_service}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={inputStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Commission Carte Bancaire"
              name="comission_cb"
              type="number"
              value={formData.comission_cb}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={inputStyles}
              error={!!errors.comission_cb}
              helperText={errors.comission_cb}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Commission Espèces"
              name="comission_cash"
              type="number"
              value={formData.comission_cash}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={inputStyles}
              error={!!errors.comission_cash}
              helperText={errors.comission_cash}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="ID Boutique"
              name="store_id"
              value={formData.store_id}
              onClick={handleStoreIdClick}
              fullWidth
              variant="outlined"
              sx={inputStyles}
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '56px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.pre_commande}
                    onChange={handleChange}
                    name="pre_commande"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#21ccbb' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#21ccbb' },
                    }}
                  />
                }
                label="Pré-commande"
                sx={{ color: '#2D2D2D', fontWeight: 500 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.fidelite_service}
                    onChange={handleChange}
                    name="fidelite_service"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#21ccbb' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#21ccbb' },
                    }}
                  />
                }
                label="Service de Fidélité"
                sx={{ color: '#2D2D2D', fontWeight: 500 }}
              />
            </Box>
          </Grid>
          {formData.fidelite_service && (
            <Grid item xs={12}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Pourcentage de Fidélité"
                    name="prc_fidelite"
                    type="number"
                    value={formData.prc_fidelite}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    sx={inputStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Jours Max Fidélité"
                    name="max_jour_fidelite"
                    type="number"
                    value={formData.max_jour_fidelite}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    sx={inputStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Date de Fidélité"
                    name="date_fidelite"
                    type="date"
                    value={formData.date_fidelite}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    sx={inputStyles}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      ),
    },
    {
      label: 'Informations de Localisation',
      content: (
        <Grid container spacing={3} sx={{ minHeight: { xs: '400px', md: '500px' }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                sx={selectStyles}
                error={!!errors.type}
              >
                <MenuItem value="restaurant">Restaurant</MenuItem>
                <MenuItem value="patisserie">Pâtisserie</MenuItem>
              </Select>
              {errors.type && (
                <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                  {errors.type}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Ville</InputLabel>
              <Select
                label="Ville"
                name="ville"
                value={formData.ville}
                onChange={handleChange}
                sx={selectStyles}
                error={!!errors.ville}
              >
                {villes.map((ville) => (
                  <MenuItem key={ville.id} value={ville.name}>
                    {ville.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.ville && (
                <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                  {errors.ville}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="ID Ville"
              name="ville_id"
              value={formData.ville_id}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={inputStyles}
              disabled
              error={!!errors.ville_id}
              helperText={errors.ville_id}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Latitude"
              name="lat"
              value={formData.lat}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={inputStyles}
              error={!!errors.lat}
              helperText={errors.lat}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Longitude"
              name="lng"
              value={formData.lng}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={inputStyles}
              error={!!errors.lng}
              helperText={errors.lng}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Adresse"
              name="adresse"
              value={formData.adresse}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={inputStyles}
              error={!!errors.adresse}
              helperText={errors.adresse}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2, height: '56px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.livraison}
                    onChange={handleChange}
                    name="livraison"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#21ccbb' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#21ccbb' },
                    }}
                  />
                }
                label="Livraison"
                sx={{ color: '#2D2D2D', fontWeight: 500 }}
              />
            </Box>
          </Grid>
        </Grid>
      ),
    },
    {
      label: "Informations de l'Entreprise",
      content: (
        <Grid container spacing={3} sx={{ minHeight: { xs: '400px', md: '500px' }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nom de la Société"
              name="nom_societe"
              value={formData.nom_societe}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={inputStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="RIB"
              name="rib"
              value={formData.rib}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={inputStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={inputStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="ICE"
              name="ice"
              value={formData.ice}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={inputStyles}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Paramètres Additionnels',
      content: (
        <Grid container spacing={3} sx={{ minHeight: { xs: '400px', md: '500px' }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Numéro de Téléphone"
              name="num_telephone"
              value={formData.num_telephone}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={inputStyles}
              error={!!errors.num_telephone}
              helperText={errors.num_telephone}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Lien Google Maps"
              name="lien_google_maps"
              value={formData.lien_google_maps}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={inputStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Moyen de Paiement"
              name="moyen_de_paiement"
              value={formData.moyen_de_paiement}
              fullWidth
              variant="outlined"
              sx={inputStyles}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Lien Facebook"
              name="lien_facebook"
              value={formData.lien_facebook}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={inputStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Lien Instagram"
              name="lien_instagram"
              value={formData.lien_instagram}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              sx={inputStyles}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2, height: '56px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.emporter}
                    onChange={handleChange}
                    name="emporter"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#21ccbb' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#21ccbb' },
                    }}
                  />
                }
                label="À Emporter"
                sx={{ color: '#2D2D2D', fontWeight: 500 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Qui Sommes-Nous"
              name="quiSommesNous"
              value={formData.quiSommesNous}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              sx={inputStyles}
            />
          </Grid>
        </Grid>
      ),
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 3, md: 6 },
        bgcolor: '#F7F9FC',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <StyledPaper>
        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              mb: 4,
              bgcolor: '#fff',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              p: 2,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Stepper
              activeStep={activeStep}
              orientation="horizontal"
              sx={{
                '& .MuiStepLabel-label': {
                  fontWeight: 500,
                  color: '#2D2D2D',
                  fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' },
                  transition: 'all 0.3s ease',
                  maxWidth: '100px',
                  whiteSpace: 'normal',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  mt: 0.5,
                },
                '& .MuiStepLabel-label.Mui-active': {
                  color: '#21ccbb',
                  fontWeight: 600,
                },
                '& .MuiStepLabel-label.Mui-completed': {
                  color: '#4CAF50',
                },
                '& .MuiStepConnector-line': {
                  borderColor: '#E0E0E0',
                  borderWidth: 2,
                  borderTopWidth: 3,
                  marginTop: '20px',
                },
                '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                  borderColor: '#21ccbb',
                },
                '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                  borderColor: '#4CAF50',
                },
                '& .MuiStep-root': {
                  bgcolor: 'transparent',
                  borderRadius: '12px',
                  transition: 'background-color 0.3s ease',
                  padding: { xs: '4px', sm: '6px', md: '8px' },
                  minWidth: { xs: '80px', sm: '100px', md: '120px' },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                },
                '& .MuiStepLabel-root': {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                },
                '& .MuiStepLabel-iconContainer': {
                  paddingRight: 0,
                  marginBottom: '4px',
                },
              }}
            >
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={CustomStepIcon}
                    onClick={handleStep(index)}
                    sx={{
                      cursor: 'pointer',
                      '& .MuiStepLabel-iconContainer': {
                        transform: 'scale(0.9)',
                      },
                      '& .MuiStepLabel-labelContainer': {
                        maxWidth: '100px',
                        textAlign: 'center',
                      },
                    }}
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Box
            sx={{
              p: 3,
              bgcolor: '#fff',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              maxHeight: { xs: '600px', md: '600px' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
              {steps[activeStep].content}
            </Box>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <StyledButton disabled={activeStep === 0 || isSubmitting} onClick={handleBack}>
                Précédent
              </StyledButton>
              {activeStep === steps.length - 1 ? (
                <StyledButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Créer le Restaurant'}
                </StyledButton>
              ) : (
                <StyledButton onClick={handleNext} disabled={isSubmitting}>
                  Suivant
                </StyledButton>
              )}
            </Box>
          </Box>
        </form>
      </StyledPaper>

      {/* Search Email Modal */}
      <StyledDialog
        open={searchModalOpen}
        onClose={handleSearchModalClose}
        TransitionComponent={Fade}
        transitionDuration={300}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 600, color: '#2D2D2D' }}>
          Rechercher un compte par email
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            label="Text"
            value={emailSearch}
            onChange={(e) => setEmailSearch(e.target.value)}
            fullWidth
            variant="outlined"
            sx={inputStyles}
            error={!!searchError}
            helperText={searchError}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleEmailSearch();
              }
            }}
          />
          <StyledButton
            onClick={handleEmailSearch}
            disabled={searchLoading}
            sx={{ mt: 2, width: '100%' }}
          >
            {searchLoading ? <CircularProgress size={24} color="inherit" /> : 'Rechercher'}
          </StyledButton>
          {searchResults.length > 0 && (
            <List sx={{ mt: 2, maxHeight: '200px', overflowY: 'auto' }}>
              {searchResults.map((account) => (
                <ListItem
                  key={account.id_caisse}
                  button
                  onClick={() => handleSelectAccount(account)}
                  sx={{
                    borderRadius: '8px',
                    mb: 1,
                    '&:hover': {
                      bgcolor: '#E6F7F5',
                    },
                  }}
                >
                  <ListItemText
                    primary={account.email}
                    secondary={`ID Caisse: ${account.id_caisse}`}
                    primaryTypographyProps={{ fontWeight: 500, color: '#2D2D2D' }}
                    secondaryTypographyProps={{ color: '#6B7280' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <StyledButton
            onClick={handleSearchModalClose}
            sx={{
              background: 'linear-gradient(45deg, #FF6F61 30%, #FF8A80 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #E63946 30%, #FF6F61 90%)',
              },
            }}
          >
            Annuler
          </StyledButton>
        </DialogActions>
      </StyledDialog>

      {/* Success/Error Dialog */}
      <StyledDialog
        open={dialog.open}
        onClose={handleDialogClose}
        TransitionComponent={Fade}
        transitionDuration={300}
        maxWidth="xs"
      >
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {dialog.severity === 'success' ? (
            <CheckCircle sx={{ fontSize: 50, color: '#21ccbb', mb: 2 }} />
          ) : (
            <Info sx={{ fontSize: 50, color: '#FF6F61', mb: 2 }} />
          )}
          <Typography
            variant="h6"
            sx={{
              color: '#2D2D2D',
              fontWeight: 600,
              mb: 1,
              textAlign: 'center',
            }}
          >
            {dialog.severity === 'success' ? 'Succès' : 'Erreur'}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#6B7280',
              textAlign: 'center',
              maxWidth: '300px',
            }}
          >
            {dialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <StyledButton
            onClick={handleDialogClose}
            sx={{
              padding: '8px 24px',
              fontSize: '0.9rem',
              background: dialog.severity === 'success'
                ? 'linear-gradient(45deg, #21ccbb 30%, #26e0cc 90%)'
                : 'linear-gradient(45deg, #FF6F61 30%, #FF8A80 90%)',
              '&:hover': {
                background: dialog.severity === 'success'
                  ? 'linear-gradient(45deg, #1ab7a8 30%, #21ccbb 90%)'
                  : 'linear-gradient(45deg, #E63946 30%, #FF6F61 90%)',
              },
            }}
          >
            Fermer
          </StyledButton>
        </DialogActions>
      </StyledDialog>
    </Box>
  );
};

export default CreateRestaurant;